import React, { createContext, useContext, useState, useEffect } from 'react';
import { apiClient } from '../api/client';

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [token, setToken] = useState(localStorage.getItem('token'));

    useEffect(() => {
        if (!token) return;

        let cancelled = false;

        const ping = async () => {
            try {
                await apiClient.post('/auth/ping');
            } catch (_e) {
                // ignore — ping failure is non-critical
            }
        };

        ping();
        const interval = setInterval(() => {
            if (cancelled) return;
            ping();
        }, 30000);

        return () => {
            cancelled = true;
            clearInterval(interval);
        };
    }, [token]);

    useEffect(() => {
        const initAuth = async () => {
            const storedToken = localStorage.getItem('token');
            const storedUser = localStorage.getItem('user');

            if (storedToken && storedUser) {
                try {
                    // Verify token is still valid by calling /api/auth/me
                    const response = await apiClient.get('/auth/me', {
                        headers: {
                            Authorization: `Bearer ${storedToken}`,
                        },
                    });

                    apiClient.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
                    setUser(response.data.data);
                    setToken(storedToken);
                } catch (error) {
                    // Token is invalid, clear storage
                    delete apiClient.defaults.headers.common['Authorization'];
                    localStorage.removeItem('token');
                    localStorage.removeItem('user');
                    setToken(null);
                    setUser(null);
                }
            }

            setLoading(false);
        };

        initAuth();
    }, []);

    const login = async ({ identifier, password }) => {
        try {
            const response = await apiClient.post('/auth/login', { identifier, password });
            const { data } = response.data;
            const { token: newToken, user: userData } = data;

            localStorage.setItem('token', newToken);
            localStorage.setItem('user', JSON.stringify(userData));

            apiClient.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
            setToken(newToken);
            setUser(userData);

            return { success: true };
        } catch (error) {
            return {
                success: false,
                error: error.response?.data?.message || 'Login failed'
            };
        }
    };

    const register = async (userData) => {
        try {
            const response = await apiClient.post('/auth/register', userData);
            const { data } = response.data;
            const { token: newToken, user: userDataPayload } = data;

            localStorage.setItem('token', newToken);
            localStorage.setItem('user', JSON.stringify(userDataPayload));

            apiClient.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
            setToken(newToken);
            setUser(userDataPayload);

            return { success: true };
        } catch (error) {
            return {
                success: false,
                error: error.response?.data?.message || 'Registration failed'
            };
        }
    };

    const logout = () => {
        delete apiClient.defaults.headers.common['Authorization'];
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setToken(null);
        setUser(null);
    };

    const updateUser = (userData) => {
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
    };

    // Sync React state when localStorage is cleared externally
    // (e.g., by the 401 interceptor or from another browser tab)
    useEffect(() => {
        const handleUnauthorized = () => {
            delete apiClient.defaults.headers.common['Authorization'];
            setToken(null);
            setUser(null);
        };
        
        const onStorage = (e) => {
            if (e.key === 'token' && !e.newValue) {
                handleUnauthorized();
            }
        };

        window.addEventListener('auth:unauthorized', handleUnauthorized);
        window.addEventListener('storage', onStorage);
        
        return () => {
            window.removeEventListener('auth:unauthorized', handleUnauthorized);
            window.removeEventListener('storage', onStorage);
        };
    }, []);

    const value = {
        user,
        token,
        loading,
        login,
        register,
        logout,
        updateUser,
        isAuthenticated: !!user,
    };

    return (
        <AuthContext.Provider value={value}>
            {loading ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: '#f8fafc' }}>
                    <div style={{ width: 40, height: 40, border: '3px solid #e5e7eb', borderTopColor: '#0b4334', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                </div>
            ) : children}
        </AuthContext.Provider>
    );
};
